// new-stream-thunk.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { showSnackbar } from "./snackbar";
import { BASE_URL, getBaseUrl } from "../config";
import { getAuthApiHeaders } from "../../utils/common";
import secureLocalStorage from "react-secure-storage";

const baseUrl = getBaseUrl();

// SSE parser: split SSE events, extract data: lines.
// Event vocabulary:
// - answer_text / token -> incremental prose, pushed into chunks
// - answer_data         -> tables/chart payload, after the prose
// - final               -> terminal event; commits the message
// - clarification       -> HITL question; the UI posts `resume` back
// Anything else with an `event` field is skipped, so a future
// backend event never renders as raw JSON in the typewriter.
const parseSseBuffer = (buffer) => {
  const parts = buffer.split("\n\n");
  const remainder = parts.pop(); // last part may be incomplete
  const chunks = [];
  let finalEvent = null;
  let clarificationEvent = null;
  let dataEvent = null;

  for (const raw of parts) {
    const lines = raw.split("\n").map((l) => l.replace(/\r$/, ""));
    const dataLines = lines
      .filter((l) => l.startsWith("data:"))
      .map((l) => l.slice(5).trimStart());

    if (!dataLines.length) continue;

    const joined = dataLines.join("\n");

    // If joined looks like JSON try to parse
    if (joined.startsWith("{") && joined.endsWith("}")) {
      try {
        const obj = JSON.parse(joined);

        if (obj && obj.event === "final") {
          finalEvent = obj;
          continue; // don't push JSON into text chunks
        }

        if (obj && obj.event === "clarification") {
          clarificationEvent = obj;
          continue; // don't push JSON into text chunks
        }

        // Incremental prose, emitted before the terminal `final` event.
        // `token` is accepted as an alias so either backend name works.
        if (obj && (obj.event === "answer_text" || obj.event === "token") && obj.content) {
          chunks.push(obj.content);
          continue;
        }

        // Tables and charts, which arrive after the prose.
        if (obj && obj.event === "answer_data") {
          dataEvent = obj;
          continue;
        }

        // Any other structured event is metadata, not prose. Without this
        // guard it would fall through below and render as raw JSON inside
        // the typewriter.
        if (obj && obj.event) continue;
      } catch (e) {
        // fallthrough -> treat as text
      }
    }

    for (const d of dataLines) chunks.push(d);
  }

  return { remainder, chunks, finalEvent, clarificationEvent, dataEvent };
};

// CREATE SESSION (POST /session)
export const newChatSession = createAsyncThunk(
  "newChatSession",
  async (opts = {}, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.post(
        `${baseUrl}/session`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
            "use-case": "src",
            ...getAuthApiHeaders({
              includeUserEmail: false,
              includeIncomingEmail: true,
            }),
            client: opts.client ? opts.client : "UT",
          },
          withCredentials: true,
        },
      );

      return response.data;
    } catch (error) {
      dispatch(
        showSnackbar({
          message: "Failed to create session!",
          severity: "error",
        }),
      );
      return rejectWithValue(
        error?.response?.statusText || error.message || "Request failed",
      );
    }
  },
);

// STREAM /chat
export const newchatfetchStreamData = createAsyncThunk(
  "data/fetchStreamData",
  async (payload = {}, { dispatch }) => {
    const headers = {
      "Content-Type": "application/json",
      "use-case": "src",
      ...getAuthApiHeaders({ includeIncomingEmail: true }),
      client: payload.client ? payload.client : "UT",
    };

    try {
      const endpoint = payload.endpoint || "chat";

      const response = await fetch(`${baseUrl}/${endpoint}`, {
        method: "POST",
        headers,
        body: JSON.stringify(payload.payload),
        credentials: "include",
      });

      if (!response.ok) {
        console.error("Network response was not ok:", response.statusText);
        dispatch(
          showSnackbar({ message: "Chat request failed", severity: "error" }),
        );
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        const {
          remainder,
          chunks,
          finalEvent,
          clarificationEvent,
          dataEvent,
        } = parseSseBuffer(buffer);
        buffer = remainder;

        if (chunks.length) {
          for (const c of chunks) dispatch(addData(c));
        }

        if (dataEvent) {
          dispatch(setDataEvent(dataEvent));
        }

        if (clarificationEvent) {
          dispatch(setClarificationEvent(clarificationEvent));
          // IMPORTANT: do NOT break; server may keep streaming,
          // but UI can decide to pause display once it sees clarification.
        }

        if (finalEvent) {
          dispatch(setFinalEvent(finalEvent));
          // don't break; continue to drain stream
        }
      }

      // Trailing leftover: a block the stream ended without terminating.
      // Parsed through parseSseBuffer rather than dispatched raw, because
      // raw-dispatching pushed any structured event still sitting here
      // (a `final`, or an `answer_text`) into the text chunks, where the
      // typewriter rendered it as JSON.
      if (buffer.trim()) {
        const {
          chunks: tailChunks,
          finalEvent: tailFinal,
          clarificationEvent: tailClarification,
          dataEvent: tailData,
        } = parseSseBuffer(buffer + "\n\n");

        for (const c of tailChunks) dispatch(addData(c));
        if (tailData) dispatch(setDataEvent(tailData));
        if (tailClarification)
          dispatch(setClarificationEvent(tailClarification));
        if (tailFinal) dispatch(setFinalEvent(tailFinal));
      }
    } catch (error) {
      dispatch(showSnackbar({ message: "Streaming error", severity: "error" }));
      console.error("Fetch error:", error?.message || error);
    }
  },
);

const dataSlice = createSlice({
  name: "data",
  initialState: {
    data: [],
    loading: false,
    streamingEnded: false,
    sessionId: "",
    finalEvent: null,
    clarificationEvent: null,
    dataEvent: null,
  },
  reducers: {
    // Tables/chart payload that arrives after the prose. Held separately
    // from finalEvent so the UI can render a table as soon as it lands,
    // rather than waiting for the terminal event.
    setDataEvent(state, action) {
      state.dataEvent = action.payload;
    },
    addData(state, action) {
      state.data.push(action.payload);
    },
    setFinalEvent(state, action) {
      state.finalEvent = action.payload;
    },
    setClarificationEvent(state, action) {
      state.clarificationEvent = action.payload;
    },
    clearStreamData(state, action) {
      state.dataEvent = null;
      state.streamingEnded = action.payload;
      state.data = [];
      state.finalEvent = null;
      state.clarificationEvent = null; // ✅ NEW
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(newChatSession.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(newChatSession.fulfilled, (state, action) => {
        state.sessionId = action.payload?.session_id || "";
        state.loading = false;
      })
      .addCase(newChatSession.rejected, (state, action) => {
        state.loading = false;
        state.isError = true;
        state.error = action.payload;
      })
      .addCase(newchatfetchStreamData.pending, (state) => {
        state.loading = true;
        state.data = [];
        state.finalEvent = null;
        state.clarificationEvent = null; // ✅ NEW
      })
      .addCase(newchatfetchStreamData.fulfilled, (state) => {
        state.loading = false;
        state.streamingEnded = true;
      })
      .addCase(newchatfetchStreamData.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const {
  addData,
  clearStreamData,
  setFinalEvent,
  setClarificationEvent,
  setDataEvent,
} = dataSlice.actions;

export default dataSlice.reducer;
